import mmmIcon from "@assets/images/Illustration-mediamix.svg";
import { ModernSelect } from "@components/Select";
import ExternalVarCell from "@components/Table/ExternalVarCell";
import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { FORECAST_PERIOD, useTimePeriods } from "@hooks";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  SelectChangeEvent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useClientModelVariables } from "@services/api";
import { IVariableValues } from "@utils/external_effects";
import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const initializeStartPeriod = (
  timePeriods: string[],
  weekIds: string[]
): string => {
  if (timePeriods.length === 0) {
    return "0";
  }

  const startPeriod = Math.min(
    ...timePeriods.map((timePeriod) =>
      weekIds.findIndex((weekId) => weekId === timePeriod)
    )
  );
  return startPeriod.toString();
};

const initializeEndPeriod = (
  timePeriods: string[],
  weekIds: string[]
): string => {
  if (timePeriods.length === 0) {
    return FORECAST_PERIOD.toString();
  }
  const periodIndexes = timePeriods.map((timePeriod) =>
    weekIds.findIndex((weekId) => weekId === timePeriod)
  );
  const endPeriod = Math.max(...periodIndexes) + 1;
  return endPeriod.toString();
};

export interface StepExternalVarsProps {
  timePeriods: string[];
  setTimePeriods: Dispatch<SetStateAction<string[]>>;
  variableValues: IVariableValues;
  setVariableValues: Dispatch<SetStateAction<IVariableValues>>;
}
const StepExternalVars = ({
  timePeriods,
  setTimePeriods,
  variableValues,
  setVariableValues
}: StepExternalVarsProps) => {
  const { currentClient } = useContext(ClientContext);
  const { t } = useTranslation();
  const timePeriodIds = useTimePeriods(currentClient.model.aggregation_type);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const iconVisibility = isTablet ? "" : "hidden";
  const iconJustify = isMobile ? "justify-left" : "justify-center";

  const [startPeriod, setStartPeriod] = useState(
    initializeStartPeriod(timePeriods, timePeriodIds)
  );
  const [endPeriod, setEndPeriod] = useState(
    initializeEndPeriod(timePeriods, timePeriodIds)
  );

  const { data: modelVars, isFetching: isLoading } = useClientModelVariables(
    currentClient.model.id,
    "external"
  );

  const handleChangeStartPeriod = (event: SelectChangeEvent) => {
    const startIndex = event.target.value as string;
    setStartPeriod(startIndex);
    setTimePeriods([
      ...timePeriodIds.slice(Number(startIndex), Number(endPeriod))
    ]);
  };
  const handleChangeEndPeriod = (event: SelectChangeEvent) => {
    const endIndex = event.target.value as string;
    setEndPeriod(endIndex);
    setTimePeriods([
      ...timePeriodIds.slice(Number(startPeriod), Number(endIndex))
    ]);
  };

  const endTimePeriods = useMemo(() => {
    const selectedStartIndex = Number(startPeriod);
    return timePeriodIds
      .map((weekId, index) => ({
        value: (index + 1).toString(),
        label: weekId
      }))
      .filter(
        (_, index) =>
          index >= selectedStartIndex &&
          index < selectedStartIndex + FORECAST_PERIOD
      );
  }, [startPeriod, timePeriodIds]);

  const hasExternalVars = useMemo(() => {
    return (modelVars || []).length > 0;
  }, [modelVars]);
  // Los options ahora se generan directamente en ModernSelect

  const tableHeaders = useMemo(
    () =>
      timePeriods.map((timePeriod) => (
        <StyledTableCell
          align="center"
          key={`new-scenario-external-var-${timePeriod}`}
        >
          {timePeriod}
        </StyledTableCell>
      )),
    [timePeriods]
  );

  const tableRows = useMemo(
    () =>
      (modelVars || []).map((modelVar) => {
        return (
          <StyledTableRow
            key={`new-${currentClient.model.id}-${modelVar.id}`}
            sx={{
              "&:last-child td, &:last-child th": { border: 0 }
            }}
          >
            <StyledTableCell align="center">{modelVar.name}</StyledTableCell>
            {timePeriods.map((timePeriod) => {
              return (
                <ExternalVarCell
                  key={`new-scenario-external-var-row-${timePeriod}-${modelVar.id}`}
                  componentId="new-scenario"
                  timePeriod={timePeriod}
                  modelVar={modelVar}
                  extVariableValues={variableValues}
                  setExtVariableValues={setVariableValues}
                  enabled={true}
                />
              );
            })}
          </StyledTableRow>
        );
      }),
    [
      timePeriods,
      setVariableValues,
      modelVars,
      currentClient.model.id.toString(),
      variableValues
    ]
  );

  return (
    <Box className="w-full mt-8">
      <Box className="w-full ml-4">
        <Typography variant="h6" component="div">
          {t("simulator.new_scenario.time_period")}
        </Typography>
        <Box className="w-1/3 flex mt-2">
          <Box sx={{ width: "100%" }}>
            <ModernSelect
              options={timePeriodIds.map((weekId, index) => ({
                value: index.toString(),
                label: weekId
              }))}
              value={startPeriod}
              onChange={handleChangeStartPeriod}
              id="start-period-select"
              valueKey="value"
              labelKey="label"
              colorTheme="light"
              fullWidth
            />
          </Box>
          <Box sx={{ width: "100%", ml: 2 }}>
            <ModernSelect
              options={endTimePeriods}
              value={endPeriod}
              onChange={handleChangeEndPeriod}
              id="end-period-select"
              valueKey="value"
              labelKey="label"
              colorTheme="light"
              fullWidth
            />
          </Box>
        </Box>
      </Box>
      {hasExternalVars && (
        <TableContainer
          className="w-full p-4 overflow-y-scroll"
          sx={{
            maxHeight: "calc(100vh - 650px)",
            minHeight: 550,
            overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
            scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
            "&::-webkit-scrollbar": {
              display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
            }
          }}
        >
          <Box className={`w-full flex pt-4 ${iconVisibility} ${iconJustify}`}>
            <SwipeIcon
              sx={{
                bottom: 12,
                opacity: 0.4,
                left: "50%",
                transform: "translate(750%, 0%)",
                color: theme.palette.primary.main
              }}
              fontSize="small"
            />
          </Box>
          <Table
            className="w-full"
            aria-label="new scenario external vars table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">
                  {t("simulator.scenario.table.external_variables.variable")}
                </StyledTableCell>
                {tableHeaders}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRowsSkeleton
                  id={`${currentClient.model.id.toString()}-steps-external-vars-table-skeleton`}
                  numberOfRows={tableRows.length}
                  columns={timePeriods.length + 1}
                />
              ) : (
                tableRows
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!hasExternalVars && (
        <Box className="w-full">
          <Box sx={{ justifyItems: "center" }}>
            <img
              className="w-full md:w-[346px] max-w-full h-auto block"
              src={mmmIcon}
              alt="MMix Icon"
            />
          </Box>

          <Typography variant="h5" component="div">
            <Box sx={{ textAlign: "center" }}>
              {t("simulator.new_scenario.no_external_vars")}
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  );
};
export default StepExternalVars;
